import Styles from './Topics.module.scss'
import { FormikArrayTextAreaInput } from 'components/FirstTimeExperienceModal/steps/BrandProfile/FormikArrayTextAreaInput'
import { startCase } from 'lodash'
import { useState } from 'react'
import { FieldArray, useField } from 'formik'
import { Button } from 'blaze-ui/Button'
import { Plus, ArrowRefresh } from 'blaze-ui/icons'
import { ButtonProps } from 'blaze-ui/Button/Types'
import { mergeProps } from '@react-aria/utils'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { useIsOnboardingRedesign } from 'hooks/useIsOnboardingRedesign'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'

interface Props {
  name: string
  minValue?: number
  description?: string
  isRegenerating?: boolean
  onRegenerate?: () => void
}

interface AddTopicButtonProps extends ButtonProps {
  name: string
}

const AddTopicButton = ({
  name,
  variant = 'tertiary',
  size = 'md',
  frontIcon = Plus,
  ...rest
}: AddTopicButtonProps) => {
  const [field] = useField<string[]>(name)

  return (
    <FieldArray name={name}>
      {({ push }) => (
        <div className={Styles.addTopicButtonContainer}>
          <Button
            variant={variant}
            size={size}
            frontIcon={Plus}
            isDisabled={field.value.some((value) => value.trim().length === 0)}
            aria-label="Add topic"
            {...mergeProps({ onPress: () => push('') }, rest)}
          >
            Add Topic
          </Button>
        </div>
      )}
    </FieldArray>
  )
}

interface RegenerateButtonProps {
  isRegenerating?: boolean
  onRegenerate: () => void
}

const RegenerateButton = ({ isRegenerating, onRegenerate }: RegenerateButtonProps) => {
  return (
    <div className={Styles.regenerateButtonContainer}>
      <Button
        variant="tertiary"
        size="md"
        frontIcon={ArrowRefresh}
        color="var(--selected-fill)"
        isDisabled={isRegenerating}
        onPress={onRegenerate}
        aria-label="Regenerate topics"
      >
        {isRegenerating ? 'Regenerating...' : 'Regenerate'}
      </Button>
    </div>
  )
}

export const Topics = ({ name, minValue = 0, description, isRegenerating, onRegenerate }: Props) => {
  const [showMore, setShowMore] = useState(false)
  const isOnboardingRedesign = useIsOnboardingRedesign()
  const { trackTopicAdded, trackTopicDeleted } = useOnboardingTracking()

  const [field] = useField<string[]>(name)
  const hasTopics = field.value.length > 0

  return (
    <div className={Styles.topics} role="group" aria-label={startCase(name)}>
      <div className={Styles.topicsContainer}>
        <div className={Styles.header}>
          <div className={Styles.headerTop}>
            <Heading level={isOnboardingRedesign ? 3 : 5}>Topics</Heading>
            <div className={Styles.actions}>
              {onRegenerate && hasTopics && (
                <RegenerateButton isRegenerating={isRegenerating} onRegenerate={onRegenerate} />
              )}
              <AddTopicButton name={name} onPress={() => setShowMore(true)} size={isOnboardingRedesign ? 'sm' : 'md'} />
            </div>
          </div>
          {description && (
            <Text variant="secondary" color="var(--dark-60)">
              {description}
            </Text>
          )}
        </div>

        <FormikArrayTextAreaInput
          name={name}
          minValue={minValue}
          showMore={showMore}
          onShowMore={() => setShowMore(true)}
          onShowLess={() => setShowMore(false)}
          onRemove={(text, name) => {
            if (text.length > 0) {
              trackTopicDeleted(text, name)
            }
          }}
          onAdd={(text, name) => {
            if (text.length > 0) {
              trackTopicAdded(text, name)
            }
          }}
          size="xl"
          disabled={isRegenerating}
        />
      </div>
    </div>
  )
}
